// Прокрутка барабанов: контракт эндпоинта /api/spin.
// Структуры повторяют записи бэкенда в src/Backend/Game/SpinResult.cs.
//
// Один запрос играет весь раунд: оплаченный спин и бесплатные респины за ним. Респин даёт
// wild, см. Nudge Respin в GDD, раздел 4.3. Решений между респинами игрок не принимает,
// поэтому спрашивать бэкенд заново не о чем: клиент проигрывает готовую последовательность.

import type { Cents, SymbolId } from './gameConfig';
import { postJson } from './http';
import type { SessionState } from './session';

const SPIN_URL = '/api/spin';

/** Ряд поля: символы барабанов слева направо. */
export type FieldRow = readonly SymbolId[];

/**
 * Поле после остановки барабанов: ряды сверху вниз.
 *
 * Поле идёт отдельно от позиций остановки, а не собирается по ним на клиенте: дальше
 * фичи будут подменять символы прямо на поле, и по одной полосе итог уже не собрать.
 */
export type Field = readonly FieldRow[];

/**
 * Выигрыш по одной линии.
 *
 * Выплата дана в центах и уже пересчитана бэкендом под ставку игрока, см. ADR-33.
 */
export interface LineWin {
  /** Номер линии в описании игры. */
  readonly payline: number;

  /** Символ комбинации. */
  readonly symbol: SymbolId;

  /** Сколько символов подряд от левого края вошло в оплаченную комбинацию. */
  readonly count: number;

  /** Выплата за комбинацию. */
  readonly payout: Cents;

  /** Ряды выигрышных ячеек слева направо. Номер барабана — это место в списке. */
  readonly positions: readonly number[];

  /** Во сколько раз надстройка умножила выплату. Единица — выплата своя. */
  readonly multiplier: number;
}

/** Ячейка поля: барабан слева направо и ряд сверху вниз. */
export interface Cell {
  readonly reel: number;
  readonly row: number;
}

/**
 * Выигрыш по всему экрану: скаттеры платят, где бы они ни легли.
 *
 * Своей линии у него нет, поэтому и дорожки на показе тоже нет: светятся сами символы.
 * За три скаттера игра платит x1 общей ставки, см. GDD, раздел 2.
 */
export interface ScatterWin {
  /** Символ, который заплатил. */
  readonly symbol: SymbolId;

  /** Сколько таких символов лежит на поле. */
  readonly count: number;

  /** Выплата. */
  readonly payout: Cents;

  /** Ячейки, где лежат эти символы. */
  readonly cells: readonly Cell[];
}

/**
 * Число, которое надстройка повесила на ячейку поля.
 *
 * Метка едет с ячейкой: барабан со сдвигом уносит её на ряд вниз, а с нижнего ряда она уходит
 * за край поля. Так значение множителя держится за своим символом всю цепочку респинов,
 * см. Nudge Respin в GDD, раздел 4.3.
 */
export interface CellMark {
  readonly reel: number;
  readonly row: number;
  readonly value: number;
}

/** Шаг манометра на одном спине. */
export interface GaugeStep {
  /** Номер манометра, он же номер барабана слева направо. */
  readonly reel: number;
  readonly from: number;
  readonly to: number;
}

/**
 * Бонусный барабан, открытый спином.
 *
 * Позиция идёт вместе с номером: открытая створка показывает полосу сразу, а состояние
 * после раунда не говорит, каким спином барабан открылся.
 */
export interface OpenedReel {
  readonly reel: number;
  readonly stop: number;
}

/**
 * Что фича Steam Spin сделала на одном спине.
 *
 * Этого по полю не вывести: открыт ли барабан и на сколько сдвинулся манометр, знает только
 * хранимое состояние сессии.
 */
export interface SteamSpinSpin {
  /** Бонусные барабаны, открытые этим спином. Пустой список — ни один. */
  readonly opened: readonly OpenedReel[];

  /** Шаги манометров на этом спине. Пустой список — ни один не сдвинулся. */
  readonly charged: readonly GaugeStep[];
}

/** Итог одной прокрутки барабанов. */
export interface SpinResult {
  /** Позиция остановки каждого барабана: индекс символа полосы, попавшего в верхний ряд. */
  readonly stops: readonly number[];

  /** Поле после остановки. */
  readonly field: Field;

  /** Выигрыши по линиям. Пустой список — проигрыш. */
  readonly wins: readonly LineWin[];

  /**
   * Барабаны, которые пришли сюда сдвигом на символ вниз, а не прокруткой.
   * Пустой список — обычный спин, крутятся все.
   */
  readonly nudged: readonly number[];

  /** Числа, которые надстройка повесила на ячейки поля. Пустой список — надстройки нет. */
  readonly marks: readonly CellMark[];

  /** Выигрыш по всему экрану. Нет поля — скаттеров на поле не набралось. */
  readonly scatter?: ScatterWin;

  /** Что фича Steam Spin сделала на этом спине. Нет поля — фичи в игре нет. */
  readonly steamSpin?: SteamSpinSpin;
}

/**
 * Выплата за один спин: все его линии вместе со скаттером.
 *
 * Бэкенд её не присылает: на раунде стоит одна сумма на все спины вместе с бонусом. Показ же
 * называет выигрыш каждого спина по отдельности, см. GDD, раздел 3.4.
 */
export function spinPayout(spin: SpinResult): Cents {
  const lines: Cents = spin.wins.reduce((total: Cents, win: LineWin) => total + win.payout, 0);

  return lines + (spin.scatter?.payout ?? 0);
}

/**
 * Чем бонус мини-слота запущен.
 *
 * steamSpin — манометр дошёл до потолка, заряженный множителями, см. GDD, раздел 5.2.
 * redHotSpin — скаттеры на экране перегрели манометры, см. GDD, раздел 6.1.
 */
export type BonusTrigger = 'steamSpin' | 'redHotSpin';

/**
 * Бонус мини-слота: один спин доски 1x3, см. GDD, разделы 5.3 и 6.2.
 *
 * Бонус идёт раз за раунд и не переживает его: он играется в конце раунда и тут же возвращает
 * фичу в начало. Поэтому он лежит на итоге раунда, а не в состоянии сессии.
 *
 * Запусков у бонуса два, а сам он один: и Steam Spin, и Red Hot Spin крутят ту же доску тем же
 * путём. Клиент читает запуск, чтобы выбрать попап, см. GDD, раздел 7.2.
 */
export interface BonusSpin {
  /** Итог спина мини-слота: позиции полос, поле и выигрыши уже с общим множителем. */
  readonly spin: SpinResult;

  /** Общий множитель: сумма зарядов трёх манометров на миг запуска. */
  readonly multiplier: number;

  /** Выплата за бонус, уже с общим множителем. Её показывает попап завершения. */
  readonly payout: Cents;

  /** Чем бонус запущен. */
  readonly trigger: BonusTrigger;

  /**
   * Шаги манометров, которые сделал сам запуск. Пустой список — их зарядили спины раунда.
   *
   * Так идёт перегрев Red Hot Spin: стрелки уходят на потолок уже после стартового попапа,
   * а не на том спине, где выпали скаттеры, см. GDD, раздел 6.2.
   */
  readonly charged: readonly GaugeStep[];
}

/** Итог раунда: оплаченный спин и бесплатные респины за ним. */
export interface RoundResult {
  /** Номер раунда. По нему бэкенд повторяет раунд точь-в-точь, вместе со всеми респинами. */
  readonly seed: number;

  /** Спины раунда по порядку. Первый оплачен игроком, остальные бесплатны. */
  readonly spins: readonly SpinResult[];

  /** Выплата за весь раунд вместе с бонусом. Она уже зачислена на счёт игрока. */
  readonly payout: Cents;

  /** Состояние игрока до раунда. Нет поля — раунд сыгран без сессии. */
  readonly before?: SessionState;

  /** Состояние игрока после раунда. Разница с before и есть то, что раунд сделал. */
  readonly after?: SessionState;

  /** Бонус мини-слота, который раунд запустил. Нет поля — бонуса в раунде не было. */
  readonly bonus?: BonusSpin;
}

/**
 * Заказ на раунд: что тестировщик хочет увидеть на экране, см. ADR-32.
 *
 * Ждать эти раунды долго: манометр копится десятки спинов, а три скаттера выпадают раз
 * на триста раундов. Играет заказ бэкенд, клиент только называет его в запросе.
 */
export type Cheat = 'wild' | 'multiplier' | 'steamSpin' | 'redHotSpin';

/** Все заказы по порядку. Из них собирается консоль тестировщика, см. game/cheats.ts. */
export const CHEATS: readonly Cheat[] = ['wild', 'multiplier', 'steamSpin', 'redHotSpin'];

/**
 * Просит бэкенд сыграть раунд.
 *
 * @param round Номер раунда. По нему запрос повторяется без потери спина: бэкенд помнит
 * итог раунда и на второй запрос с тем же номером отдаёт его же, а не крутит новый спин.
 * Повтор не двигает и состояние фич: манометр не заряжается дважды за один спин.
 * @param session Номер сессии. Раунд идёт от её состояния и оставляет за собой новое.
 * @param bet Ставка за спин. Бэкенд списывает её со счёта и считает от неё выплату.
 * @param cheat Заказ на раунд. Пусто — раунд идёт своим чередом.
 */
export function requestSpin(round: string, session: string, bet: Cents, cheat?: Cheat): Promise<RoundResult> {
  const ordered: string = cheat === undefined ? '' : `&cheat=${cheat}`;

  return postJson<RoundResult>(`${SPIN_URL}?session=${session}&round=${round}&bet=${bet}${ordered}`);
}
